import React, { useState, useRef, useEffect } from 'react';

/**
 * 🚀 ОПТИМИЗИРОВАННЫЙ КОМПОНЕНТ ИЗОБРАЖЕНИЙ
 * - Lazy loading с Intersection Observer
 * - Поддержка WebP формата
 * - Адаптивные размеры
 * - Прогрессивная загрузка
 */
const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className = '',
  style = {},
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIzNSIgeT0iMzUiIHdpZHRoPSIzMCIgaGVpZ2h0PSIzMCI+CjxjaXJjbGUgY3g9IjE1IiBjeT0iMTUiIHI9IjEwIiBmaWxsPSIjZDFkNWRiIi8+CjwvY2lyY2xlPgo8L3N2Zz4K',
  priority = false,
  sizes = '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw',
  fetchpriority,
  onLoad,
  onError,
  ...imgProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const [currentSrc, setCurrentSrc] = useState(priority ? src : placeholder);

  // Intersection Observer для lazy loading
  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Загрузка изображения когда оно попадает в видимость
  useEffect(() => {
    if (!isInView || isLoaded) return;

    // Проверяем поддержку WebP один раз и кешируем
    let webpSupport = false;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      webpSupport = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (e) {
      webpSupport = false;
    }

    // Формируем оптимальный URL
    const getOptimizedSrc = (originalSrc) => {
      if (!originalSrc) return placeholder;
      
      // Если это upload файл, добавляем параметры оптимизации
      if (originalSrc.includes('/uploads/')) {
        const url = new URL(originalSrc, window.location.origin);
        
        // Добавляем параметры для оптимизации на бэкенде
        if (width) url.searchParams.set('w', width);
        if (height) url.searchParams.set('h', height);
        
        // Запрашиваем WebP если поддерживается
        if (webpSupport) {
          url.searchParams.set('format', 'webp');
        }
        
        return url.toString();
      }
      
      return originalSrc;
    };

    const optimizedSrc = getOptimizedSrc(src);
    
    // Предзагрузка изображения
    const img = new Image();
    
    img.onload = () => {
      setCurrentSrc(optimizedSrc);
      setIsLoaded(true);
      setHasError(false);
    };
    
    img.onerror = () => {
      // Fallback к оригинальному изображению
      if (optimizedSrc !== src) {
        const fallbackImg = new Image();
        fallbackImg.onload = () => {
          setCurrentSrc(src);
          setIsLoaded(true);
          setHasError(false);
        };
        fallbackImg.onerror = () => {
          setHasError(true);
        };
        fallbackImg.src = src;
      } else {
        setHasError(true);
      }
    };
    
    img.src = optimizedSrc;
  }, [isInView, src, width, height, placeholder, isLoaded]);

  // Стили для плавной анимации
  const imageStyle = {
    transition: 'opacity 0.3s ease-in-out, filter 0.3s ease-in-out',
    opacity: isLoaded ? 1 : 0.7,
    filter: isLoaded ? 'none' : 'blur(1px)',
    ...style
  };

  if (hasError) {
    return (
      <div
        ref={imgRef}
        className={`${className} bg-gray-200 flex items-center justify-center`}
        style={{ width, height, ...style }}
      >
        <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
        </svg>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={imageStyle}
      sizes={sizes}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchpriority={fetchpriority}
      onLoad={(e) => {
        if (!isLoaded) setIsLoaded(true);
        onLoad?.(e);
      }}
      onError={(e) => {
        setHasError(true);
        onError?.(e);
      }}
      {...imgProps}
    />
  );
};

export default OptimizedImage;
